import { useDisclosure } from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SortableTh from "../../components/Core/SortableTh/SortableTh";
import VirtualizedTableLayout from "../../components/Layout/VirtualizedTableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import { useListFilters } from "../../components/Shared/Filters/useListFilters";
import ColumnResizeHandle from "../../components/Shared/ListColumns/ColumnResizeHandle";
import { ListColumn } from "../../components/Shared/ListColumns/types";
import { useColumnResize } from "../../components/Shared/ListColumns/useColumnResize";
import { useListColumns } from "../../components/Shared/ListColumns/useListColumns";
import ListPageToolbar from "../../components/Shared/ListPageToolbar/ListPageToolbar";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import ListViewTagChips, {
	hasListViewTypes,
} from "../../components/Shared/TagChip/ListViewTagChips";
import WeeklyAvailability from "../../components/Shared/WeeklyAvailability/WeeklyAvailability";
import { ConsultantBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import useDebounce from "../../hooks/useDebounce";
import { ConsultantsListResponse } from "../../models/Consultant";
import {
	ACTUAL_AVAILABILITY_DAYS,
	EMPTY_DEFINITION,
	FilterDefinition,
	definitionsEqual,
	getActualAvailability,
	isEmptyDefinition,
	matchesDefinition,
	resolveAvailabilityDates,
} from "../../models/ConsultantFilter";
import { SORT_ORDER } from "../../models/Sorting";
import { useBookedConsultantIds, useConsultants } from "../../networking/consultant.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { useSubsidiaries } from "../../networking/subsidiary.hooks";
import { cn } from "../../utils/cn";
import { isCompletePersonalNumber } from "../../utils/ValidationRules";
import { consultantRoute } from "../../utils/route.utils";
import ConsultantFilterPanel from "./components/ConsultantFilterPanel";
import { useFilterData } from "./components/useFilterData";

/**
 * Consultant list rendered with the virtualized table layout (the /consultants
 * page).
 *
 *  - Loads the *entire* filtered set up-front (no server pagination) so the
 *    whole list lives in one virtualized scroll container.
 *  - Search and sort happen client-side over the in-memory list.
 *
 * Filtering is owned by the shared `useListFilters` hook and column visibility
 * by `useListColumns`; this page supplies only its declarative column set (which
 * drives the header, rows, sorting and the column picker) and the data source.
 */

const ConsultantsListVirtualizedPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const filters = useListFilters<FilterDefinition>({
		filtersKey: "consultantFilters",
		definitionsEqual,
		isEmptyDefinition,
		emptyDefinition: EMPTY_DEFINITION,
	});
	const {
		pool,
		setPool,
		searchTerm,
		definition,
		setDefinition,
		savedFilters,
		orgFilters,
		selectedSavedId,
		handleSaveNew,
		handleUpdateSaved,
		handleRenameSaved,
		handleDeleteSaved,
		handleReorderSaved,
	} = filters;

	// Sort is keyed by column `key` (not an enum) so a column owns its own sort.
	const [sort, setSort] = useState<string | undefined>(undefined);
	const [order, setOrder] = useState<SORT_ORDER>(SORT_ORDER.DEFAULT);

	const filterPanel = useDisclosure();

	const keyword = useDebounce(searchTerm, 250);

	// Personal numbers are never part of the cached list (it is persisted in the
	// browser), so a complete personnummer is resolved server-side to a set of ids
	// and the cached list is narrowed to those. Anything else is searched locally.
	const isSsnSearch = isCompletePersonalNumber(keyword || "");
	const { consultantList: ssnMatches, isFetching: isFetchingSsnMatches } = useConsultants(
		{
			page: 1,
			pageSize: 1,
			keyword: (keyword || "").trim(),
			pool: pool || undefined,
			view: "select_option",
		},
		isSsnSearch
	);
	const ssnMatchIds = useMemo(
		() => (isSsnSearch ? new Set((ssnMatches?.users || []).map((u) => u.id)) : undefined),
		[isSsnSearch, ssnMatches]
	);

	// React Query owns the full list per pool. Filter/search/sort are client-side
	// over the cached result; staleTime keeps back-navigation instant. The same
	// hook derives the competence tree, the panel's options and the manager names.
	const {
		consultants: allConsultants,
		options: filterOptions,
		competenceTree,
		managerNameById,
		isLoading,
	} = useFilterData(pool);

	// The "available (not booked)" filter works on relative days, so the concrete
	// dates are resolved here. Recomputed every render but memoized on the day
	// itself, so identities stay stable — and a dashboard left open over midnight
	// picks up the new "today" on its next render.
	const currentDates = resolveAvailabilityDates();
	const availabilityDates = useMemo(
		() => currentDates,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[currentDates.today]
	);

	// Bookings are fetched separately from the consultant list (which is cached by
	// sync version and must stay time-independent) and only while the filter is on.
	// One request covers every selectable day, so toggling days doesn't refetch.
	const { busyDatesByConsultant, isLoading: isLoadingBookings } = useBookedConsultantIds(
		availabilityDates[ACTUAL_AVAILABILITY_DAYS[0]],
		availabilityDates[ACTUAL_AVAILABILITY_DAYS[ACTUAL_AVAILABILITY_DAYS.length - 1]],
		Object.keys(getActualAvailability(definition)).length > 0
	);

	// Tag/cert types opted in to list views (org settings) gate the "tags" column:
	// it only exists — and appears in the column picker — when something is opted in.
	const { data: organisation } = useOrganisation();
	const consultantTagTypes = organisation?.consultantTagTypes;
	const certificationTypes = organisation?.certificationTypes;
	const tagsAvailable = hasListViewTypes(consultantTagTypes, certificationTypes);

	// The subsidiary column only exists (and appears in the picker) when the
	// organisation has subsidiaries.
	const { subsidiaries } = useSubsidiaries();
	const subsidiaryNameById = useMemo(
		() => new Map(subsidiaries.map((s) => [s.id, s.name])),
		[subsidiaries]
	);

	// The declarative column set. `employeeNo` is mandatory; manager is opt-in
	// (default off). Weights are relative — a visible column's width is its weight
	// over the sum of visible weights (and the user can resize them).
	const columns = useMemo<ListColumn<ConsultantsListResponse>[]>(
		() => [
			{
				key: "employeeNo",
				label: "EMPLOYEE_NO",
				weight: 10,
				mandatory: true,
				pinned: true,
				headerClassName: "pl-6",
				cellClassName: "pl-6",
				sortValue: (c) =>
					c.profile?.employeeNumber != null ? String(c.profile.employeeNumber) : "",
				renderCell: (c) => c.profile?.employeeNumber,
			},
			{
				key: "firstName",
				label: "FIRST_NAME",
				weight: 17,
				sortValue: (c) => c.firstName || "",
				renderCell: (c) => (
					<div className="flex items-center gap-1">
						<span className="truncate">{c.firstName}</span>
					</div>
				),
			},
			{
				key: "lastName",
				label: "LAST_NAME",
				weight: 17,
				sortValue: (c) => c.lastName || "",
				renderCell: (c) => c.lastName,
			},
			{
				key: "email",
				label: "EMAIL",
				weight: 16,
				renderCell: (c) => c.email,
			},
			{
				key: "phone",
				label: "PHONE",
				weight: 12,
				stopClickPropagation: true,
				renderCell: (c) => (
					<a className="text-action hover:underline" href={"tel:" + c.profile?.phoneNumber}>
						{c.profile?.phoneNumber}
					</a>
				),
			},
			{
				key: "manager",
				label: "MANAGER",
				weight: 14,
				defaultVisible: false,
				renderCell: (c) =>
					c.profile?.manager?.id ? managerNameById.get(c.profile.manager.id) : "",
			},
			{
				key: "city",
				label: "CITY",
				weight: 12,
				defaultVisible: false,
				sortValue: (c) => c.profile?.address?.city?.trim() || "",
				renderCell: (c) => c.profile?.address?.city,
			},
			{
				key: "subsidiary",
				label: "organisation.subsidiaries.title",
				weight: 12,
				available: subsidiaryNameById.size > 0,
				defaultVisible: false,
				sortValue: (c) =>
					(c.profile?.subsidiaryId && subsidiaryNameById.get(c.profile.subsidiaryId)) || "",
				renderCell: (c) =>
					c.profile?.subsidiaryId ? subsidiaryNameById.get(c.profile.subsidiaryId) : "",
			},
			{
				// Graphical week grid (the same condensed representation used in the
				// consultant sidebar). The list is already fetched with availability.
				key: "availability",
				label: "AVAILABILITY",
				weight: 20,
				defaultVisible: false,
				renderCell: (c) => (
					<WeeklyAvailability hideWeekdays condensed availability={c.profile?.availability} />
				),
			},
			{
				key: "tags",
				label: "Taggar & certifikat",
				weight: 18,
				available: tagsAvailable,
				renderCell: (c) => (
					<ListViewTagChips
						tags={c.profile?.tags}
						tagTypes={consultantTagTypes}
						certifications={c.profile?.certifications}
						certificationTypes={certificationTypes}
					/>
				),
			},
			{
				key: "status",
				label: "STATUS",
				weight: 10,
				sortValue: (c) => (c.active ? "1" : "0"),
				cellClassName: (c) => (!c.active ? "text-danger" : undefined),
				renderCell: (c) => (c.active ? t("ACTIVE") : t("INACTIVE")),
			},
		],
		[t, managerNameById, tagsAvailable, consultantTagTypes, certificationTypes, subsidiaryNameById]
	);

	const listColumns = useListColumns<ConsultantsListResponse>(
		"consultantListColumns",
		columns,
		"consultantListColumnWidths",
		"consultantListColumnOrder"
	);
	const { visibleColumns, setColumnWidths, resetColumnWidth } = listColumns;

	const resize = useColumnResize<ConsultantsListResponse>({
		columns: visibleColumns,
		onCommit: setColumnWidths,
		onReset: resetColumnWidth,
	});

	// If the sorted column gets hidden via the picker, drop the sort.
	useEffect(() => {
		if (sort && !visibleColumns.some((c) => c.key === sort)) {
			setSort(undefined);
			setOrder(SORT_ORDER.DEFAULT);
		}
	}, [visibleColumns, sort]);

	const handleSortColumn = (column: string) => {
		if (sort !== column) {
			setSort(column);
			setOrder(SORT_ORDER.ASC);
		} else {
			setOrder((prev) => (prev === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC));
		}
	};

	// Widths use the resize-aware weight so an in-progress drag previews live.
	const totalWeight = visibleColumns.reduce((sum, c) => sum + resize.weightOf(c.key), 0);
	const widthOf = (col: ListColumn<ConsultantsListResponse>) =>
		`${(resize.weightOf(col.key) / totalWeight) * 100}%`;

	// Client-side search + sort over the in-memory list.
	const rows = useMemo(() => {
		const term = (keyword || "").trim().toLowerCase();

		// No context until the bookings are in: the availability group then matches
		// nothing, so a failed fetch yields an empty list rather than passing booked
		// consultants off as available.
		const matchContext = busyDatesByConsultant
			? { busyDatesByConsultant, availabilityDates }
			: undefined;

		let result = allConsultants.filter((c) => matchesDefinition(c, definition, matchContext));

		if (ssnMatchIds) {
			// Server-side personnummer lookup; while it is in flight there is nothing
			// to show yet rather than a name search that can never match.
			result = result.filter((c) => !!c.id && ssnMatchIds.has(c.id));
		} else if (term) {
			result = result.filter((c) => {
				const haystack = [
					c.firstName,
					c.lastName,
					c.email,
					c.profile?.phoneNumber,
					c.profile?.employeeNumber,
					c.profile?.address?.city,
				]
					.filter(Boolean)
					.join(" ")
					.toLowerCase();
				return haystack.includes(term);
			});
		}

		const activeColumn = visibleColumns.find((c) => c.key === sort);
		if (activeColumn?.sortValue && order !== SORT_ORDER.DEFAULT) {
			const valueOf = activeColumn.sortValue;
			result = [...result].sort((a, b) => {
				const cmp = valueOf(a).localeCompare(valueOf(b), "sv", { numeric: true });
				return order === SORT_ORDER.ASC ? cmp : -cmp;
			});
		}

		return result;
	}, [
		allConsultants,
		definition,
		keyword,
		ssnMatchIds,
		sort,
		order,
		visibleColumns,
		busyDatesByConsultant,
		availabilityDates,
	]);

	const navigateToConsultant = (id?: string) => navigate(consultantRoute(id || ""));

	const renderTableHeader = useCallback(() => {
		return (
			<thead className="border-b-2 border-border">
				<tr className="text-left bg-muted text-sm font-medium whitespace-nowrap">
					{visibleColumns.map((col) => {
						const handle = resize.handleProps(col.key);
						return col.sortValue ? (
							<SortableTh
								key={col.key}
								label={t(col.label as any)}
								column={col.key}
								activeColumn={sort}
								order={order}
								onSort={handleSortColumn}
								width={widthOf(col)}
								className={col.headerClassName}
								resizeHandle={handle && <ColumnResizeHandle {...handle} />}
							/>
						) : (
							<th
								key={col.key}
								className={cn("relative pt-3 pb-2 px-4", col.headerClassName)}
								style={{ width: widthOf(col) }}
							>
								{t(col.label as any)}
								{handle && <ColumnResizeHandle {...handle} />}
							</th>
						);
					})}
				</tr>
			</thead>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visibleColumns, sort, order, t, resize]);

	const renderTableRow = useCallback(
		(index: number) => {
			const consultant = rows[index];
			if (!consultant) return null;

			const baseCellClass =
				"px-4 py-2 border-t border-border border-solid align-middle text-base truncate";

			return (
				<>
					{visibleColumns.map((col) => {
						const extra =
							typeof col.cellClassName === "function"
								? col.cellClassName(consultant)
								: col.cellClassName;
						return (
							<td
								key={col.key}
								className={cn(baseCellClass, extra)}
								style={{ width: widthOf(col) }}
								onClick={col.stopClickPropagation ? (e) => e.stopPropagation() : undefined}
							>
								{col.renderCell(consultant)}
							</td>
						);
					})}
				</>
			);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[rows, visibleColumns, resize]
	);

	return (
		<>
			<Breadcrumbs
				items={ConsultantBreadcrumbs}
				showPoolDropdown
				onChangePool={setPool}
				selectedPool={pool}
			/>

			<ListPageToolbar
				filters={filters}
				count={rows.length}
				onOpenFilterPanel={filterPanel.onOpen}
				createTo={routes.CONSULTANTS_NEW}
				createLabel="CREATE_CONSULTANT"
				columns={listColumns}
			/>

			<ConsultantFilterPanel
				isOpen={filterPanel.isOpen}
				onClose={filterPanel.onClose}
				definition={definition}
				onChange={setDefinition}
				options={filterOptions}
				competenceTree={competenceTree}
				savedFilters={savedFilters}
				orgFilters={orgFilters}
				activeSavedId={selectedSavedId}
				onSaveNew={handleSaveNew}
				onUpdateSaved={handleUpdateSaved}
				onRenameSaved={handleRenameSaved}
				onDeleteSaved={handleDeleteSaved}
				onReorderSaved={handleReorderSaved}
			/>

			{isLoading || isLoadingBookings || isFetchingSsnMatches ? (
				<LoadingIndicator className="mt-4" />
			) : rows.length ? (
				<VirtualizedTableLayout
					itemCount={rows.length}
					estimatedRowHeight={48}
					overscan={10}
					renderHeader={renderTableHeader}
					renderRow={renderTableRow}
					getRowProps={(index) =>
						({ "data-contact": rows[index]?.id } as React.HTMLAttributes<HTMLTableRowElement>)
					}
					onRowClick={(index) => navigateToConsultant(rows[index]?.id)}
					containerHeight="calc(100vh - 100px)"
					selectable
				/>
			) : (
				<div className="px-4">
					<EmptyState margin="2rem 0" label="NO_CONSULTANTS" />
				</div>
			)}
		</>
	);
};

export default ConsultantsListVirtualizedPage;
